import React, { useState } from 'react'
import { ExportOptions } from '../ExportOptionsModal'
import { MarketCustomColumn } from '../../../types/database'

interface PDFOptionsEditorProps {
  exportOptions: ExportOptions
  customColumns: MarketCustomColumn[]
  onUpdate: (options: ExportOptions) => Promise<void> | void
  onCancel: () => void
}

export default function PDFOptionsEditor({
  exportOptions,
  customColumns,
  onUpdate,
  onCancel
}: PDFOptionsEditorProps) {
  const [editingOptions, setEditingOptions] = useState<ExportOptions>({ ...exportOptions })
  const [isUpdating, setIsUpdating] = useState(false)

  const handleSave = async () => {
    setIsUpdating(true)
    try {
      await onUpdate(editingOptions)
    } finally {
      setIsUpdating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit PDF Options</h3>

          <div className="space-y-6">
            {/* Prepared For Field */}
            <div>
              <label htmlFor="editPreparedFor" className="block text-sm font-medium text-gray-900 mb-2">
                Prepared For
              </label>
              <input
                type="text"
                id="editPreparedFor"
                value={editingOptions.preparedFor || ''}
                onChange={(e) => setEditingOptions({ ...editingOptions, preparedFor: e.target.value })}
                placeholder="Enter client name or organization"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
              />
            </div>

            {/* Historical Data Range */}
            <div>
              <label htmlFor="editHistoricalMonths" className="block text-sm font-medium text-gray-900 mb-2">
                Historical Data Range
              </label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  id="editHistoricalMonths"
                  value={editingOptions.historicalMonths}
                  onChange={(e) => setEditingOptions({
                    ...editingOptions,
                    historicalMonths: Math.max(1, Math.min(48, parseInt(e.target.value) || 12))
                  })}
                  min="1"
                  max="48"
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                />
                <span className="text-sm text-gray-700">months</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Applies to Recently Sold and Removed from Market sections only
              </p>
            </div>

            {/* Pricing Mode Selector */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Pricing Mode</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="editPricingMode"
                    value="internal"
                    checked={editingOptions.pricingMode === 'internal'}
                    onChange={() => setEditingOptions({ ...editingOptions, pricingMode: 'internal' })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="font-semibold">Actual Ask / Take</span>
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="editPricingMode"
                    value="external"
                    checked={editingOptions.pricingMode === 'external'}
                    onChange={() => setEditingOptions({ ...editingOptions, pricingMode: 'external' })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="font-semibold">Adjusted Ask / Take</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Hide Off-Market Aircraft */}
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingOptions.hideOffMarket || false}
                  onChange={(e) => setEditingOptions({ ...editingOptions, hideOffMarket: e.target.checked })}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  <span className="font-semibold">Hide Off-Market Aircraft</span>
                </span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">
                Exclude off-market aircraft from the PDF report
              </p>
            </div>

            {/* Section Visibility */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Sections to Include</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="editSectionVisibility"
                    checked={!editingOptions.currentMarketOnly && !editingOptions.hideRemovedFromMarket}
                    onChange={() => setEditingOptions({ ...editingOptions, currentMarketOnly: false, hideRemovedFromMarket: false })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="font-semibold">All Sections</span>
                    <span className="text-gray-500 ml-1">- Current Market, Sold, and Removed</span>
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="editSectionVisibility"
                    checked={editingOptions.hideRemovedFromMarket || false}
                    onChange={() => setEditingOptions({ ...editingOptions, currentMarketOnly: false, hideRemovedFromMarket: true })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="font-semibold">Hide Removed from Market</span>
                    <span className="text-gray-500 ml-1">- Current Market and Sold only</span>
                  </span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="editSectionVisibility"
                    checked={editingOptions.currentMarketOnly || false}
                    onChange={() => setEditingOptions({ ...editingOptions, currentMarketOnly: true, hideRemovedFromMarket: false })}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    <span className="font-semibold">Current Market Only</span>
                    <span className="text-gray-500 ml-1">- Exclude Sold and Removed</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Market Overview Page */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Market Overview Page</h4>
              <div className="space-y-2">
                {(['include', 'exclude', 'only'] as const).map(option => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name="editMarketOverview"
                      checked={editingOptions.marketOverview === option}
                      onChange={() => setEditingOptions({ ...editingOptions, marketOverview: option })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      <span className="font-semibold">{option.charAt(0).toUpperCase() + option.slice(1)}</span>
                      <span className="text-gray-500 ml-1">
                        {option === 'include' && '- Show Market Overview with tables'}
                        {option === 'exclude' && '- Tables only, no Market Overview'}
                        {option === 'only' && '- Market Overview page only, no tables'}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            {/* Column Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Columns to Include</h4>
                <button
                  type="button"
                  onClick={() => {
                    const allSelected = Object.values(editingOptions.columns).every(Boolean)
                    const newValue = !allSelected
                    setEditingOptions({
                      ...editingOptions,
                      columns: {
                        serialNumber: newValue,
                        owner: newValue,
                        broker: newValue,
                        yearMfrDlv: newValue,
                        aftt: newValue,
                        emp: newValue,
                        damage: newValue,
                        pax: newValue,
                        paintInterior: newValue,
                        options: newValue,
                        relativeValue: newValue,
                        dateListed: newValue,
                        askingPrice: newValue,
                        takePrice: newValue,
                        custom1: newValue,
                        custom2: newValue,
                        custom3: newValue
                      }
                    })
                  }}
                  className="text-sm text-purple-600 hover:text-purple-800"
                >
                  {Object.values(editingOptions.columns).every(Boolean) ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'serialNumber', label: 'Serial Number' },
                  { key: 'owner', label: 'Owner' },
                  { key: 'broker', label: 'Broker' },
                  { key: 'yearMfrDlv', label: 'Manufactured / Delivered Year' },
                  { key: 'aftt', label: 'AFTT' },
                  { key: 'emp', label: 'EMP' },
                  { key: 'damage', label: 'DMG' },
                  { key: 'pax', label: 'PAX' },
                  { key: 'paintInterior', label: 'Paint / Interior' },
                  { key: 'options', label: 'Options' },
                  { key: 'relativeValue', label: 'Comparative Values' },
                  { key: 'dateListed', label: 'Date Listed' },
                  { key: 'askingPrice', label: 'Asking Price' },
                  { key: 'takePrice', label: 'Take Price' }
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingOptions.columns[key as keyof typeof editingOptions.columns]}
                      onChange={() => setEditingOptions({
                        ...editingOptions,
                        columns: {
                          ...editingOptions.columns,
                          [key]: !editingOptions.columns[key as keyof typeof editingOptions.columns]
                        }
                      })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Columns */}
            {customColumns.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Custom Columns</h4>
                <div className="grid grid-cols-2 gap-3">
                  {customColumns.map((column) => {
                    const key = `custom${column.column_index}` as keyof typeof editingOptions.columns
                    return (
                      <label key={column.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={editingOptions.columns[key]}
                          onChange={() => setEditingOptions({
                            ...editingOptions,
                            columns: {
                              ...editingOptions.columns,
                              [key]: !editingOptions.columns[key]
                            }
                          })}
                          className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">{column.column_title}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                disabled={isUpdating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!Object.values(editingOptions.columns).some(Boolean) || isUpdating}
                className="px-6 py-2 text-sm font-medium text-white bg-purple-700 border border-transparent rounded-md hover:bg-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isUpdating ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Updating Preview...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Update</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

